/**
 * The backend rejects an identifier save that collides with an existing record
 * (e.g. an EIN already used by another lead) with HTTP 406 and a body like:
 *
 * {
 *   "responsedata": "Duplicate data found!",
 *   "status": 406,
 *   "responsemsg": [{ "code": "enr_101", "desc": "Duplicate data found!" }]
 * }
 *
 * The same body can arrive either as the `HttpErrorResponse.error` payload or -
 * when the gateway relays it with a 200 - as the success body carrying
 * `status: 406`, so the check works off whichever object the caller holds.
 */
import { DUPLICATE_IDENTIFIER_FIELD_LABELS } from '../constants/static-data.constants';

export const DUPLICATE_IDENTIFIER_CODE = 'enr_101';

interface IdentifierResponsePayload {
  status?: number;
  responsedata?: unknown;
  responsemsg?: { code?: string; desc?: string }[] | null;
}

const isDuplicateText = (value: unknown): boolean =>
  typeof value === 'string' && /duplicate/i.test(value);

/** True when the response/error body is the backend's duplicate-identifier rejection. */
export function isDuplicateIdentifierPayload(payload: unknown): boolean {
  if (!payload) return false;
  if (typeof payload === 'string') return isDuplicateText(payload);
  if (typeof payload !== 'object') return false;

  const body = payload as IdentifierResponsePayload;
  const messages = Array.isArray(body.responsemsg) ? body.responsemsg : [];
  const hasDuplicateMessage = messages.some(
    (msg) =>
      String(msg?.code ?? '').toLowerCase() === DUPLICATE_IDENTIFIER_CODE ||
      isDuplicateText(msg?.desc)
  );

  return hasDuplicateMessage || isDuplicateText(body.responsedata);
}

/** True when a failed HTTP call is the duplicate-identifier rejection. */
export function isDuplicateIdentifierError(error: unknown): boolean {
  return isDuplicateIdentifierPayload((error as { error?: unknown })?.error);
}

/**
 * The dups-check endpoint (e.g. `dupsCheck/referral_participant`) can flag more than one
 * colliding identifier in a single call. It packs them into one `desc` string after a
 * " - " separator, e.g. "Duplicate data found! - CIN: 8527410, ITIN: 963525633". This pulls
 * out just the identifier type codes (`["CIN", "ITIN"]`) so callers can key form errors by field.
 */
export function getDuplicateIdentifierFields(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];

  const body = payload as IdentifierResponsePayload;
  const messages = Array.isArray(body.responsemsg) ? body.responsemsg : [];
  // Strict code match only - the fuzzy `isDuplicateText` fallback also matches a benign
  // "No duplicate data found!" success message (it just checks for the substring "duplicate"),
  // which would otherwise make a clean dups-check look like a rejection.
  const desc = messages.find(
    (msg) => String(msg?.code ?? '').toLowerCase() === DUPLICATE_IDENTIFIER_CODE
  )?.desc;

  if (typeof desc !== 'string') return [];

  const separatorIndex = desc.indexOf(' - ');
  if (separatorIndex === -1) return [];

  return desc
    .slice(separatorIndex + 3)
    .split(',')
    .map((entry) => entry.split(':')[0]?.trim())
    .filter((type): type is string => !!type);
}

const getDuplicateIdentifierFieldLabel = (field: string): string =>
  DUPLICATE_IDENTIFIER_FIELD_LABELS[field] ?? field;

/** Builds a field-keyed, human-readable message for each identifier the backend flagged as a duplicate. */
export function buildDuplicateIdentifierFieldErrors(payload: unknown): Record<string, string> {
  return getDuplicateIdentifierFields(payload).reduce<Record<string, string>>((errors, field) => {
    const label = getDuplicateIdentifierFieldLabel(field);
    errors[field] = `The ${label} entered is a duplicate. This ${label} already exists on file for another record.`;
    return errors;
  }, {});
}
